import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchModels, createModel, deleteModel, arxivaModel, assignaModel } from '../store/modelSlice';
import {
  PlusIcon, MagnifyingGlassIcon, ArchiveBoxIcon, ArchiveBoxArrowDownIcon,
  TrashIcon, UserGroupIcon,
} from '@heroicons/react/24/outline';
import modelService from '../services/modelService';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

// ELS TIPUS QUE ES PODEN CREAR.
//
// **`hybrid` se n'ha anat del tot** (27/08/2026). Acoblar dinàmica de sistemes amb agents és una
// pràctica real, però **només val la pena quan les dues meitats existeixen i són bones**, i aquí
// no n'hi havia ni una: era un nom a un enum, sense taula, sense model de Sequelize i sense una
// línia de codi. Un tipus que no fa res no és una promesa, és una trampa per a qui el tria.
//
// **`agent_based` es queda a posta**, marcat com a «encara no»: la seva pantalla explica què és i
// què aportaria. No és una façana —no fa veure que funciona— sinó **una nota que no es perdi**:
// és la peça que falta per poder ensenyar l'emergència, i val la pena que estigui a la vista.
const TIPUS = ['stock_flow', 'causal_loop', 'soft_systems', 'agent_based'];
const ENCARA_NO = ['agent_based'];

function DialegAssignar({ model, alumnes, t, esAdmin, onTanca, onDesa }) {
  const [triats, setTriats] = React.useState(model.sharedWith || []);
  const commuta = (id) =>
    setTriats((v) => (v.includes(id) ? v.filter((x) => x !== id) : [...v, id]));

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-md p-6 w-full max-w-md flex flex-col max-h-[80vh]">
        <h3 className="text-lg font-semibold text-gray-900">{t('models.assignarTitol')}</h3>
        <p className="mt-1 text-sm text-gray-600">{t('models.assignarCos', { nom: model.name })}</p>

        <div className="mt-4 flex-1 min-h-0 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
          {/* Un administrador no té «alumnes assignats» —els assigna ell— i dir-li que se
              n'esperi els seus era absurd. El que li passa és una altra cosa: no hi ha ningú
              més al sistema. */}
          {!alumnes.length && (
            <p className="p-4 text-sm text-gray-500">
              {t(esAdmin ? 'models.capAltreCompte' : 'models.capAlumne')}
            </p>
          )}
          {alumnes.map((a) => (
            <label key={a.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={triats.includes(a.id)} onChange={() => commuta(a.id)}
                className="h-4 w-4 rounded-md border-gray-300 text-primary-700 focus:ring-primary-600" />
              <span className="min-w-0">
                <span className="block text-sm text-gray-900 truncate">{a.name}</span>
                <span className="block text-xs text-gray-500 truncate">{a.email}</span>
              </span>
            </label>
          ))}
        </div>

        <p className="mt-3 text-xs text-gray-500">{t('models.assignarNota')}</p>

        <div className="mt-4 flex justify-end gap-3">
          <button type="button" onClick={onTanca}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
            {t('common.cancel')}
          </button>
          <button type="button" onClick={() => onDesa(triats)}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-700 rounded-md hover:bg-primary-800">
            {t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

const Models = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { models, loading } = useSelector((state) => state.models);
  const { user } = useSelector((state) => state.auth);
  const potAssignar = user?.role === 'instructor' || user?.role === 'admin';
  // Els arxivats i els actius són dues llistes excloents, no un filtre a sobre de la mateixa:
  // el servidor et dona els uns o els altres.
  const [veientArxivats, setVeientArxivats] = useState(false);
  const [aEsborrar, setAEsborrar] = useState(null);
  const [aAssignar, setAAssignar] = useState(null);
  const [alumnes, setAlumnes] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // El tauler enllaça a `/models?nou=1` per obrir el diàleg de crear directament: un botó que
  // digui «Model nou» i et deixi davant d'una llista no fa el que diu. El paràmetre es treu
  // tot seguit, que si no, tornar enrere el tornaria a obrir.
  const [params, setParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  const [newModel, setNewModel] = useState({
    name: '',
    type: 'stock_flow',
    description: '',
    isPublic: false,
  });

  useEffect(() => {
    dispatch(fetchModels({ page: 1, arxivats: veientArxivats ? 'true' : undefined }));
  }, [dispatch, veientArxivats]);

  useEffect(() => {
    if (params.get('nou') === '1') {
      setShowCreateModal(true);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  useEffect(() => {
    if (potAssignar) modelService.elsMeusAlumnes().then(setAlumnes).catch(() => setAlumnes([]));
  }, [potAssignar]);

  const arxiva = async (model, arxivat) => {
    const r = await dispatch(arxivaModel({ id: model.id, arxivat }));
    if (arxivaModel.fulfilled.match(r)) toast.success(t(arxivat ? 'models.arxivat' : 'models.desarxivat'));
    else toast.error(t('models.arxivaFallat'));
  };

  const esborra = async () => {
    const r = await dispatch(deleteModel(aEsborrar.id));
    setAEsborrar(null);
    if (deleteModel.fulfilled.match(r)) toast.success(t('models.esborrat'));
    else toast.error(t('models.esborraFallat'));
  };

  const desaAssignacio = async (usuaris) => {
    const r = await dispatch(assignaModel({ id: aAssignar.id, usuaris }));
    setAAssignar(null);
    if (assignaModel.fulfilled.match(r)) toast.success(t('models.assignat'));
    else toast.error(t('models.assignaFallat'));
  };

  const handleCreateModel = async (e) => {
    e.preventDefault();
    const result = await dispatch(createModel(newModel));
    if (createModel.fulfilled.match(result)) {
      toast.success(t('models.created'));
      setShowCreateModal(false);
      setNewModel({ name: '', type: 'stock_flow', description: '', isPublic: false });
      navigate(`/models/${result.payload.id}`);
    } else {
      toast.error(t('models.createFailed'));
    }
  };

  const filteredModels = models.filter(model => {
    const matchesSearch = model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          model.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || model.type === filterType;
    return matchesSearch && matchesType;
  });

  // Cinc tipus amb cinc colors no era una categoria, era decoració: el color no deia res que
  // l'etiqueta no digués ja, i gastava el verd i el groc, que aquí volen dir «correcte» i
  // «avís». L'etiqueta es llegeix; el color el porten l'estoc i el flux.
  const modelTypeColors = {
    stock_flow: 'bg-gray-100 text-gray-700',
    causal_loop: 'bg-gray-100 text-gray-700',
    agent_based: 'bg-gray-100 text-gray-700',
    soft_systems: 'bg-gray-100 text-gray-700',
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('models.title')}</h1>
        <p className="mt-2 text-gray-600">{t('models.subtitle')}</p>
      </div>

      <div className="mb-4 flex items-center gap-1 border-b border-gray-200">
        {[[false, 'models.actius'], [true, 'models.arxivats']].map(([val, clau]) => (
          <button key={clau} type="button" onClick={() => setVeientArxivats(val)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              veientArxivats === val
                ? 'border-primary-700 text-primary-800'
                : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t(clau)}
          </button>
        ))}
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
            <input
              type="text"
              placeholder={t('models.search')}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <select
          className="px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="all">{t('models.allTypes')}</option>
          {TIPUS.map((v) => (
            <option key={v} value={v}>
              {t(`modelTypes.${v}`)}{ENCARA_NO.includes(v) ? ` — ${t('editor.encaraNo')}` : ''}
            </option>
          ))}
        </select>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          {t('models.newModel')}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {!filteredModels.length && (
            <div className="sm:col-span-2 lg:col-span-3 border border-dashed border-gray-300 rounded-md p-10 text-center">
              <p className="text-gray-700">{models.length ? t('models.noMatch') : t('models.empty')}</p>
              {!models.length && <p className="text-sm text-gray-500 mt-1">{t('models.emptyHint')}</p>}
            </div>
          )}
          {filteredModels.map((model) => (
            <div
              key={model.id}
              className="bg-white rounded-md shadow hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/models/${model.id}`)}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{model.name}</h3>
                  <span className={`px-2 py-1 text-xs rounded-full ${modelTypeColors[model.type]}`}>
                    {t(`modelTypes.${model.type}`)}
                  </span>
                </div>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {model.description || t('models.noDescription')}
                </p>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>{t('models.version', { n: model.version })}</span>
                  <span>{new Date(model.updatedAt).toLocaleDateString()}</span>
                </div>

                {/* Les accions van SOTA una línia i no dins la targeta clicable: la targeta
                    sencera obre el model, i un botó que obrís una altra cosa sense avisar és
                    com es fan els clics equivocats. `stopPropagation` a totes. */}
                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-1">
                  {model.sharedWith?.length > 0 && (
                    <span className="text-[11px] text-gray-500 mr-auto flex items-center gap-1">
                      <UserGroupIcon className="h-3.5 w-3.5" />
                      {t('models.assignatA', { n: model.sharedWith.length })}
                    </span>
                  )}
                  {model.creatorId !== user?.id && (
                    <span className="text-[11px] text-gray-500 mr-auto">{t('models.noEsTeu')}</span>
                  )}
                  <div className="ml-auto flex items-center gap-1">
                    {potAssignar && model.creatorId === user?.id && (
                      <button type="button" title={t('models.assigna')} aria-label={t('models.assigna')}
                        onClick={(e) => { e.stopPropagation(); setAAssignar(model); }}
                        className="p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100">
                        <UserGroupIcon className="h-4 w-4" />
                      </button>
                    )}
                    {model.creatorId === user?.id && (
                      <>
                        <button type="button"
                          title={t(veientArxivats ? 'models.desarxiva' : 'models.arxiva')}
                          aria-label={t(veientArxivats ? 'models.desarxiva' : 'models.arxiva')}
                          onClick={(e) => { e.stopPropagation(); arxiva(model, !veientArxivats); }}
                          className="p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100">
                          {veientArxivats ? <ArchiveBoxArrowDownIcon className="h-4 w-4" />
                                          : <ArchiveBoxIcon className="h-4 w-4" />}
                        </button>
                        <button type="button" title={t('models.esborra')} aria-label={t('models.esborra')}
                          onClick={(e) => { e.stopPropagation(); setAEsborrar(model); }}
                          className="p-1.5 rounded-md text-gray-500 hover:text-danger-700 hover:bg-danger-50">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-md p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">{t('models.createTitle')}</h2>
            <form onSubmit={handleCreateModel}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('models.name')}</label>
                  <input
                    type="text"
                    required
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                    placeholder={t('models.namePlaceholder')}
                    value={newModel.name}
                    onChange={(e) => setNewModel({ ...newModel, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('models.type')}</label>
                  <select
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                    value={newModel.type}
                    onChange={(e) => setNewModel({ ...newModel, type: e.target.value })}
                  >
                    {TIPUS.map((v) => (
            <option key={v} value={v}>
              {t(`modelTypes.${v}`)}{ENCARA_NO.includes(v) ? ` — ${t('editor.encaraNo')}` : ''}
            </option>
          ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('models.description')}</label>
                  <textarea
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                    placeholder={t('models.descriptionPlaceholder')}
                    value={newModel.description}
                    onChange={(e) => setNewModel({ ...newModel, description: e.target.value })}
                  />
                </div>
                {/* «Públic» vol dir «exemple per a tothom», i per això només surt a qui el pot
                    marcar. Abans hi era per a tothom i el servidor l'obeïa: una casella que
                    qualsevol podia clicar per saltar-se la regla de qui veu què. */}
                {potAssignar && (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isPublic"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded-md"
                      checked={newModel.isPublic}
                      onChange={(e) => setNewModel({ ...newModel, isPublic: e.target.checked })}
                    />
                    <label htmlFor="isPublic" className="ml-2 block text-sm text-gray-900">
                      {t('models.public')}
                    </label>
                  </div>
                )}
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('models.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  {t('models.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ESBORRAR demana confirmació i diu el nom, perquè no té marxa enrere. Arxivar no en
          demana: és reversible i es desfà des de la pestanya del costat. */}
      {aEsborrar && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-md p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900">{t('models.esborrarTitol')}</h3>
            <p className="mt-2 text-sm text-gray-600">
              {t('models.esborrarCos', { nom: aEsborrar.name })}
            </p>
            <p className="mt-2 text-sm text-gray-600">{t('models.esborrarAlternativa')}</p>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setAEsborrar(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                {t('common.cancel')}
              </button>
              <button type="button" onClick={esborra}
                className="px-4 py-2 text-sm font-medium text-white bg-danger-600 rounded-md hover:bg-danger-700">
                {t('models.esborra')}
              </button>
            </div>
          </div>
        </div>
      )}

      {aAssignar && (
        <DialegAssignar model={aAssignar} alumnes={alumnes} t={t}
          esAdmin={user?.role === 'admin'}
          onTanca={() => setAAssignar(null)} onDesa={desaAssignacio} />
      )}
    </div>
  );
};

export default Models;